import {
  type DealerLogo,
  type DealerLogoData,
  EXCLUDED_LOGOS,
  getDealerLogoData,
  isDealerLogoData,
  sanitizeDealerLogo
} from "@/src/utils/dealerLogoUtils";

import * as Sentry from "@sentry/react";

const EMPTY: DealerLogoData = { primary: [], secondary: [] };

// Prerender (Node) has no `window`; the browser does.
const IS_BUILD = typeof window === "undefined";

// One in-flight fetch shared by every prerendered landing page in a build.
let buildDataPromise: Promise<DealerLogoData> | undefined;

const logoSortName = (logo: DealerLogo): string =>
  logo.businessName || logo.alt || logo.fileName;

// Each entry is validated on its own, so one malformed entry is skipped instead
// of emptying the list. A fixed alphabetical order keeps the prerendered HTML
// and the first client render identical; DealerLogoCards shuffles after
// hydration.
export const prepareLogos = (logos: readonly unknown[]): DealerLogo[] =>
  logos
    .map(sanitizeDealerLogo)
    .filter(
      (logo): logo is DealerLogo =>
        logo !== undefined && !EXCLUDED_LOGOS.includes(logo.fileName)
    )
    .sort((a, b) => logoSortName(a).localeCompare(logoSortName(b), "fi"));

const resolveDealerLogoData = async (): Promise<DealerLogoData> => {
  const data = await getDealerLogoData();
  if (!isDealerLogoData(data)) {
    return EMPTY;
  }
  return {
    primary: prepareLogos(data.primary),
    secondary: prepareLogos(data.secondary)
  };
};

// Logos are secondary page content, so a fetch failure degrades to empty lists
// instead of failing the build or the navigation.
const resolveSafely = async (): Promise<DealerLogoData> => {
  try {
    return await resolveDealerLogoData();
  } catch (error) {
    if (IS_BUILD) {
      console.warn("Prerender: failed to fetch dealer logos", error);
    } else {
      Sentry.captureException(error);
    }
    return EMPTY;
  }
};

export const loadDealerLogoRouteData = (): Promise<DealerLogoData> => {
  if (!IS_BUILD) {
    return resolveSafely();
  }
  buildDataPromise ??= resolveSafely();
  return buildDataPromise;
};
